<template>
  <div>
    <FlatActionButton
      :action="handleRefresh"
      :buttonClass="['btnDefault', $style.refreshBtn]"
      text="refresh"
    />
    <LoadingOverlay :showLoadingOverlay="tableLoading" />
    <NoRecordsFound v-show="!tableLoading && !totalEventsCount" />
    <TableWithPagination
      v-show="totalEventsCount"
      :columns="columns"
      :currentPageNum="currentPageNum"
      footerDetails="events"
      :renderActionColumn="true"
      :rowsPerPage="rowsPerPage"
      showRowsPerPageMenuOnTop
      :tableData="tableData[currentPageNum]"
      :totalCount="totalEventsCount"
      @pageChange="handlePageChange"
    >
      <template
        v-slot:expandedContent="props"
      >
        <div :class="$style.jsonBlock">
          <h6>JSON</h6>
          <pre :class="$style.json">{{ props.record }}</pre>
        </div>
      </template>
    </TableWithPagination>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import { get } from 'lodash';
import { mapActions, mapState } from 'vuex';
import { notify } from 'client/util';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay.vue';
import MdmSettingsMixin from 'client/MDM/mixins/MdmSettingsMixin';
import NoRecordsFound from 'client/components/Tables/NoRecordsFound.vue';
import TableWithPagination from 'components/Tables/TableWithPagination.vue';
import moment from 'moment';

const {
  ItemTypeConsts, StoreModuleMap, MdmEventsStatus,
} = Constants;
const eventsModule = StoreModuleMap[ItemTypeConsts.mdmEvents];
const { error, success } = Constants.default;

export default {
  name: 'MdmEventsTable',

  components: {
    FlatActionButton,
    LoadingOverlay,
    NoRecordsFound,
    TableWithPagination,
  },

  mixins: [MdmSettingsMixin],

  data: () => ({
    columns: [
      { dataField: 'timestamp', name: 'Timestamp' },
      { dataField: 'request_type', name: 'Event Type' },
      { dataField: 'status', name: 'Status' },
    ],
    currentPageNum: 0,
    events: [],
    rowsPerPage: 10,
    rowsPerRequest: 50,
    tableData: [],
    tableLoading: false,
    totalEventsCount: 0,
  }),

  computed: {
    ...mapState(eventsModule, [
      'startDate',
    ]),

    daysRange() { return this.isCustomer ? 90 : 15; },
  },

  created() {
    this.handleCreated();
  },

  mounted() {
    this.fetchEvents();
  },

  methods: {
    ...mapActions(eventsModule, [
      'addFilter',
      'fetchEventsData',
      'setFilter',
      'setStartDate',
    ]),

    adjustEventsPerPage() {
      this.tableData = [];
      for (let i = 0; i <= this.totalEventsCount / this.rowsPerPage; i += 1) {
        this.tableData.push([]);
      }
      this.events.forEach((item, index) => {
        this.tableData[Math.floor(index / this.rowsPerPage)].push(this.events[index]);
      });
    },

    fetchEvents() {
      const payload = { limit: this.rowsPerRequest };

      return new Promise((resolve, reject) => {
        this.fetchEventsData(payload)
          .then((resp) => {
            this.totalEventsCount = resp.resultCount;
            this.events = resp.data;
            this.events.forEach((item, index) => {
              this.events[index].status = MdmEventsStatus[item.status];
            });
            this.adjustEventsPerPage();
            resolve();
          })
          .catch((err) => {
            notify(error, get(err, 'response.data.message', 'There was a problem retrieving events data.'));
            reject();
          });
      });
    },

    handleCreated() {
      this.setStartDate(moment().subtract(this.daysRange, 'day').toDate());
      this.setFilter({
        field: eventFields.service,
        value: ['mdm'],
      });
      this.setFilter({
        field: eventFields.mdmDeviceId,
        value: this.getSystemMdmId,
      });
    },

    handlePageChange({ currentPageNum, rowsPerPage }) {
      this.currentPageNum = currentPageNum;
      this.rowsPerPage = rowsPerPage;
      this.adjustEventsPerPage();
    },

    handleRefresh() {
      this.fetchEvents()
        .then(() => {
          notify(success, 'Events refreshed successfully');
        });
    },
  },
};
</script>

<style module>

@value (
  gradientLight6
) from '~css/colors.css';

.jsonBlock {
  margin: 10px auto;
  width: 38vw;
}

.jsonBlock .json {
  border: none;
  border-radius: 0;
  border-top: 1px solid gradientLight6;
  font-family: var(--jc-font-monospace);
  font-size: 10px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.refreshBtn {
  margin-bottom: 20px !important;
}

.tableWithPagination {
  margin: 30px 0;
}

</style>
